import * as React from "react"

import * as ContextMenuPrimitive from "@radix-ui/react-context-menu"

import { cn } from "../../cn"
import {
  dropdownMenuContentVariants,
  dropdownMenuItemVariants,
} from "../DropdownMenu/DropdownMenu"

/* ── Root ──────────────────────────────────────────────────── */

const ContextMenu = ContextMenuPrimitive.Root

/**
 * Wrap the element that owns the menu. Pass `asChild` so the element itself
 * becomes the trigger instead of gaining a wrapper `span`, which would break a
 * grid or flex parent that positions its children directly.
 */
const ContextMenuTrigger = ContextMenuPrimitive.Trigger

/* ── Content ───────────────────────────────────────────────── */

const ContextMenuContent = React.forwardRef<
  React.ComponentRef<typeof ContextMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content>
>(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Portal>
    <ContextMenuPrimitive.Content
      ref={ref}
      className={cn(dropdownMenuContentVariants(), className)}
      {...props}
    />
  </ContextMenuPrimitive.Portal>
))

ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName

/* ── Item ──────────────────────────────────────────────────── */

export interface ContextMenuItemProps extends React.ComponentPropsWithoutRef<
  typeof ContextMenuPrimitive.Item
> {
  destructive?: boolean
}

const ContextMenuItem = React.forwardRef<
  React.ComponentRef<typeof ContextMenuPrimitive.Item>,
  ContextMenuItemProps
>(({ className, destructive, ...props }, ref) => (
  <ContextMenuPrimitive.Item
    ref={ref}
    className={cn(
      dropdownMenuItemVariants(),
      destructive
        ? "text-coval-danger-text focus:bg-coval-danger-surface focus:text-coval-danger-on-surface"
        : "text-coval-text focus:bg-surface-default-hover",
      className,
    )}
    {...props}
  />
))

ContextMenuItem.displayName = ContextMenuPrimitive.Item.displayName

/* ── Separator ─────────────────────────────────────────────── */

const ContextMenuSeparator = React.forwardRef<
  React.ComponentRef<typeof ContextMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-surface-1-hover", className)}
    {...props}
  />
))

ContextMenuSeparator.displayName = ContextMenuPrimitive.Separator.displayName

/* ── Exports ───────────────────────────────────────────────── */

export {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
}
